import React, { useRef, useState } from "react";
import { Modal } from "../../../components/ui/Modal";
import { Button } from "../../../components/ui/Button";
import { Pill } from "../../../components/ui/Pill";
import type { CustomerBulkImportResult, CustomerInput } from "../customers.types";

interface ParsedCustomer extends CustomerInput {
  _valid: boolean;
  _missing: string[];
}

interface CustomerCsvImportModalProps {
  onClose: () => void;
  onImport: (rows: CustomerInput[]) => Promise<CustomerBulkImportResult>;
  open: boolean;
}

const HEADERS_MAP: Record<string, keyof CustomerInput> = {
  email: "email",
  mail: "email",
  nom: "name",
  name: "name",
  client: "name",
  telephone: "phone",
  téléphone: "phone",
  phone: "phone",
  mobile: "phone",
  ville: "city",
  city: "city",
  postalcode: "postalCode",
  "code postal": "postalCode",
  codepostal: "postalCode",
  zipcode: "postalCode",
  pays: "country",
  country: "country",
  adresse: "address",
  address: "address",
};

function splitRow(line: string): string[] {
  const separator = line.includes(";") && !line.includes(",") ? ";" : ",";
  const result: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let index = 0; index < line.length; index += 1) {
    const character = line[index];
    if (character === '"') {
      inQuotes = !inQuotes;
      continue;
    }
    if (character === separator && !inQuotes) {
      result.push(current.trim());
      current = "";
      continue;
    }
    current += character;
  }

  result.push(current.trim());
  return result;
}

function parseCustomers(raw: string): ParsedCustomer[] {
  const lines = raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length === 0) return [];

  const headerCells = splitRow(lines[0]).map((header) => header.toLowerCase().trim());
  const mappedHeaders = headerCells.map((header) => HEADERS_MAP[header]);
  const hasHeader = mappedHeaders.filter(Boolean).length >= 4;
  const startIndex = hasHeader ? 1 : 0;
  const columnKeys: Array<keyof CustomerInput | undefined> = hasHeader
    ? mappedHeaders
    : ["email", "name", "phone", "city", "postalCode", "country", "address"];

  const parsed: ParsedCustomer[] = [];
  for (let index = startIndex; index < lines.length; index += 1) {
    const cells = splitRow(lines[index]);
    const customer: ParsedCustomer = {
      email: "",
      name: "",
      phone: "",
      city: "",
      postalCode: "",
      country: "",
      address: "",
      _valid: false,
      _missing: [],
    };

    for (let cellIndex = 0; cellIndex < cells.length; cellIndex += 1) {
      const key = columnKeys[cellIndex];
      if (!key) continue;
      customer[key] = cells[cellIndex];
    }

    const missing = ["email", "name", "city", "postalCode", "country", "address"].filter((field) => {
      const value = customer[field as keyof CustomerInput];
      return typeof value !== "string" || !value.trim();
    });

    customer.email = customer.email.trim().toLowerCase();
    customer.name = customer.name.trim();
    customer.phone = customer.phone?.trim() || "";
    customer.city = customer.city.trim();
    customer.postalCode = customer.postalCode.trim();
    customer.country = customer.country.trim();
    customer.address = customer.address.trim();
    customer._missing = missing;
    customer._valid = missing.length === 0 && /^\S+@\S+\.\S+$/.test(customer.email);

    if (!customer._valid && /^\S+@\S+\.\S+$/.test(customer.email) === false && customer.email) {
      customer._missing = [...customer._missing, "email invalide"];
    }

    if (customer.email || customer.name || customer.city || customer.address) {
      parsed.push(customer);
    }
  }

  return parsed;
}

function downloadTemplate() {
  const csv = [
    "email,name,phone,city,postalCode,country,address",
    "client@exemple.fr,Maison Delorme,+33600000000,Lyon,69002,France,12 rue Mercière",
    "commande@bistrot-bleu.fr,Bistrot Bleu,+33100000000,Paris,75011,France,42 avenue Parmentier",
  ].join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "modele-import-clients.csv";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function CustomerCsvImportModal({ onClose, onImport, open }: CustomerCsvImportModalProps) {
  const [step, setStep] = useState<"intro" | "preview" | "importing" | "done">("intro");
  const [raw, setRaw] = useState("");
  const [parsed, setParsed] = useState<ParsedCustomer[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CustomerBulkImportResult | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const validRows = parsed
    .filter((row) => row._valid)
    .map(({ _valid, _missing, ...customer }) => ({
      ...customer,
      phone: customer.phone?.trim() ? customer.phone.trim() : undefined,
    }));

  const handleParse = (source?: string) => {
    setError(null);
    const next = parseCustomers(source ?? raw);
    if (next.length === 0) {
      setError("Aucune ligne détectée. Vérifie les colonnes email, name, city, postalCode, country, address.");
      return;
    }
    setParsed(next);
    setStep("preview");
  };

  const handleFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (loadEvent) => {
      const text = (loadEvent.target?.result as string) || "";
      setRaw(text);
      handleParse(text);
    };
    reader.readAsText(file, "utf-8");
    event.target.value = "";
  };

  const confirmImport = async () => {
    setStep("importing");
    setError(null);
    try {
      const importResult = await onImport(validRows);
      setResult(importResult);
      setStep("done");
    } catch (importError: any) {
      setError(importError?.message || "Impossible d'importer les clients.");
      setStep("preview");
    }
  };

  return (
    <Modal onClose={onClose} title="Import CSV · clients" wide open={open}>
      {step === "intro" && (
        <>
          <div className="mb-3.5 text-sm leading-[1.6] text-secondary">
            Importez plusieurs clients à partir d'un CSV ou collez directement les lignes ici avant prévisualisation.
          </div>

          <div className="card mb-3.5 p-3">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-xs uppercase tracking-[2px] text-tertiary">Colonnes attendues</div>
              <button
                type="button"
                onClick={downloadTemplate}
                className="border-none bg-transparent p-0 text-[11px] text-accent underline decoration-dotted underline-offset-2"
              >
                Télécharger un modèle →
              </button>
            </div>
            <div className="grid gap-2 text-[12px] sm:grid-cols-4">
              {[
                ["email", "Email client"],
                ["name", "Nom du client"],
                ["phone", "Téléphone optionnel"],
                ["city", "Ville"],
                ["postalCode", "Code postal"],
                ["country", "Pays"],
                ["address", "Adresse complète"],
              ].map(([key, hint]) => (
                <div key={key}>
                  <div className="font-mono text-accent">{key}</div>
                  <div className="mt-0.5 text-[10px] leading-[1.4] text-tertiary">{hint}</div>
                </div>
              ))}
            </div>
          </div>

          <textarea
            value={raw}
            onChange={(event) => setRaw(event.target.value)}
            rows={7}
            placeholder={"email,name,phone,city,postalCode,country,address\nclient@exemple.fr,Maison Delorme,+33600000000,Lyon,69002,France,12 rue Mercière"}
            className="input mb-3 resize-y font-mono text-[12px] leading-[1.6]"
          />

          {error && <div className="mb-2 text-[12px] text-error">{error}</div>}

          <div className="flex gap-2">
            <Button type="button" variant="secondary" className="flex-1" onClick={() => fileRef.current?.click()}>
              Choisir un fichier .csv
            </Button>
            <Pill active onClick={() => handleParse()} disabled={!raw.trim()} style={{ flex: 2 }}>
              Prévisualiser →
            </Pill>
          </div>

          <input ref={fileRef} type="file" accept=".csv,.txt" onChange={handleFile} className="hidden" />
        </>
      )}

      {step === "preview" && (
        <>
          <div className="mb-2.5 text-sm text-secondary">
            {parsed.length} ligne{parsed.length > 1 ? "s" : ""} détectée{parsed.length > 1 ? "s" : ""} · {validRows.length} valide{validRows.length > 1 ? "s" : ""}
          </div>

          <div className="mb-3 max-h-[320px] overflow-y-auto rounded-md border border-border">
            {parsed.map((row, index) => (
              <div
                key={`${row.email}-${index}`}
                className={`flex gap-3 px-3 py-2 ${index < parsed.length - 1 ? "border-b border-foreground/[0.04]" : ""}`}
              >
                <span className={`mt-1 h-2.5 w-2.5 rounded-full ${row._valid ? "bg-[#2E8B57]" : "bg-[#C0504D]"}`} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[13px] text-foreground">{row.name || "Sans nom"}</div>
                  <div className="truncate text-[11px] text-secondary">{row.email || "Email manquant"}</div>
                  <div className="truncate text-[10px] text-tertiary">{row.city} · {row.country} · {row.address}</div>
                </div>
                {!row._valid && <span className="shrink-0 text-[10px] text-error">{row._missing.join(", ")}</span>}
              </div>
            ))}
          </div>

          {error && <div className="mb-2 text-[12px] text-error">{error}</div>}

          <div className="flex gap-2">
            <Pill onClick={() => setStep("intro")} style={{ flex: 1 }}>
              ← Modifier
            </Pill>
            <Pill active onClick={confirmImport} disabled={validRows.length === 0} style={{ flex: 2 }}>
              Importer {validRows.length} client{validRows.length > 1 ? "s" : ""} →
            </Pill>
          </div>
        </>
      )}

      {step === "importing" && (
        <div className="py-8 text-center text-sm text-secondary">Import des clients en cours...</div>
      )}

      {step === "done" && result && (
        <div className="space-y-4">
          <div className="rounded-[18px] border border-border bg-surface/75 p-4">
            <div className="text-sm font-semibold text-foreground">Import terminé</div>
            <div className="mt-2 text-sm leading-[1.6] text-secondary">
              {result.created.length} client{result.created.length > 1 ? "s" : ""} créé{result.created.length > 1 ? "s" : ""} sur {result.validCount} ligne{result.validCount > 1 ? "s" : ""} valide{result.validCount > 1 ? "s" : ""}.
            </div>
            <div className="mt-1 text-xs text-tertiary">
              Doublons ignorés : {result.skippedDuplicates} · Lignes reçues : {result.receivedCount}
            </div>
          </div>

          <div className="flex justify-end">
            <Button type="button" variant="accent" onClick={onClose}>
              Fermer
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}